import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, Upload, CheckCircle, Clock, AlertCircle, CreditCard, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../utils/api';

export default function IdentityVerification() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get('/api/profile');
      setProfile(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF of your ID');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      const { data: uploadData } = await api.post('/api/upload/images', formData);
      if (uploadData.urls && uploadData.urls.length > 0) {
        await api.post('/api/profile/submit-id', { id_document_url: uploadData.urls[0] });
        toast.success('ID submitted for verification');
        fetchProfile();
      }
    } catch { toast.error('Failed to upload ID'); }
    finally { setUploading(false); }
  };

  const status = profile?.id_verification_status || 'none';

  if (loading) return <div className="animate-pulse h-32 bg-gray-100 rounded-2xl" />;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="identity-verification">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${
            status === 'verified' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
            status === 'pending' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
            'bg-gradient-to-r from-gray-400 to-gray-500'
          }`}>
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-xl">Identity Verification</h3>
            <p className="text-gray-500 text-sm">Upload a government-issued ID for account verification</p>
          </div>
        </div>
      </div>
      <div className="p-6">
        {status === 'verified' ? (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4" data-testid="id-verified-badge">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-800">Identity Verified</p>
              <p className="text-sm text-green-600">Your identity has been verified. You have full access to all features.</p>
            </div>
          </div>
        ) : status === 'pending' ? (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="id-pending-badge">
            <Clock className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Verification Pending</p>
              <p className="text-sm text-amber-600">Your ID is being reviewed. This usually takes 1-2 business days.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Why verify your identity?</p>
                <ul className="text-sm text-blue-600 mt-1 space-y-1">
                  <li>- Required for Dealer Pro features</li>
                  <li>- Enables card payments on file</li>
                  <li>- Builds trust with customers</li>
                </ul>
              </div>
            </div>
            <div className="text-center">
              <input
                ref={inputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={handleUpload}
                data-testid="id-file-input"
              />
              <Button
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl h-12 px-8"
                data-testid="upload-id-btn"
              >
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload ID Document</>}
              </Button>
              <p className="text-xs text-gray-400 mt-2">Driver's license, passport, or state ID. Max 10MB.</p>
            </div>
          </div>
        )}

        {/* Card on File section */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <CreditCard className="w-5 h-5 text-gray-600" />
            <h4 className="font-semibold text-gray-900">Payment Method on File</h4>
          </div>
          {profile?.stripe_customer_id || profile?.card_last4 ? (
            <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
              <div className="w-10 h-7 bg-gradient-to-r from-blue-700 to-blue-900 rounded-md flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {profile?.card_brand || 'Card'} ending in {profile?.card_last4 || '****'}
                </p>
                <p className="text-xs text-gray-500">Managed securely via Stripe</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              No payment method on file. A card will be saved when you subscribe to a plan.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
